import type { ClientManifest } from "@shared/contracts";
import { adoptManifest, getManifest, parseManifest, primeManifestFromCache } from "@/app/config/manifest";
import { onForegroundChange } from "@/app/platform";
import { apiRequest } from "./api-client";

/**
 * ASKING THE SERVICE WHAT THIS BUILD SHOULD BE DOING — AT LAUNCH, AND AGAIN WHEN THE APP
 * COMES BACK.
 *
 * The values themselves, their defaults and the cache live in `app/config/manifest.ts`; this
 * is the half that touches the network, and the split keeps that module readable from
 * `app/observability` without the two importing each other in a circle.
 *
 * THIS REPLACES THE VERSION-FLOOR FETCH. `/v1/meta/minimum-version` was a one-field manifest
 * answering the same question this does — "what should this build do?" — and two fetches,
 * two caches and two staleness stories can disagree about the same build. The service still
 * answers the old endpoint, because builds already on phones ask it and cannot be recalled;
 * nothing in this app calls it any more.
 */

/**
 * HOW LONG AN ANSWER STAYS GOOD, AND WHY THIS NUMBER MOVED.
 *
 * The floor check used six hours, reasoned from the floor alone: it moves at release
 * cadence, so asking more often bought nothing and cost a round trip on a connection the app
 * does not own. That reasoning does not survive the fold. The same fetch now carries the log
 * level and the replay rates, which are turned during an incident — and an hour is the
 * difference between "we changed it and watched it take effect" and "we changed it and went
 * for lunch".
 *
 * One small GET per hour of foreground returns is the cost. tontag's bridge polls at the
 * same cadence for the same reason.
 */
export const MANIFEST_REFETCH_AFTER_MS = 60 * 60 * 1000;

/**
 * ONE ROUND TRIP, AND EVERY FAILURE ENDS THE SAME WAY: NOTHING CHANGES.
 *
 * Unreachable, timed out, a 500, a captive portal's login page, a body this build cannot
 * parse — all of them leave whatever was already in force in place, which is the cached
 * answer or the permissive defaults. There is no path out of here that makes the app behave
 * worse than it did a second ago.
 *
 * The failure is not reported. A phone on a train produces hundreds of these and they would
 * drown the signal; `api-client.ts` already reports the 5xx case, which is the one somebody
 * has to fix.
 */
export async function fetchManifest(): Promise<boolean> {
  const result = await apiRequest<unknown>("/v1/meta/manifest", { timeout: 6 });
  if (!result.ok) return false;

  const manifest = parseManifest(result.data);
  if (!manifest) return false;

  await adoptManifest(manifest);
  return true;
}

/**
 * THE LAUNCH ASK, THEN ONE PER RETURN THAT IS FAR ENOUGH APART.
 *
 * A MINIMUM INTERVAL, NOT A DEBOUNCE — the same discipline the version check used, and the
 * two are not interchangeable. A debounce collapses returns that land within milliseconds of
 * each other, so every genuine return still pays for a round trip and pays for it LATE. The
 * platform seam already collapses repeated foreground states. What is left to control is how
 * OFTEN this question is worth asking, which is a property of how fast the answer changes
 * rather than of how fast the user switches apps. So the first return after the window asks
 * immediately, and every return inside it costs nothing.
 *
 * The clock is per-watcher and in memory on purpose: a cold launch asks unconditionally, and
 * a launch is exactly when the answer is most likely to have moved.
 *
 * `onManifest` fires after every attempt, successful or not, with the values actually in
 * force — so a caller that gates on the version floor is told about the cached floor even
 * when the service cannot be reached.
 */
export function watchManifest(onManifest: (manifest: ClientManifest) => void): () => void {
  /**
   * `null` rather than `0`, and that is not style: with zero, "never asked" and "asked at
   * the epoch" are the same value, and the launch ask then works only because the real clock
   * happens to be a large number. Freeze the clock in a test and the app stops asking at
   * all — which is the failure this whole function exists to remove.
   */
  let lastCheckedAt: number | null = null;
  let stopped = false;

  const refresh = () => {
    const now = Date.now();
    if (lastCheckedAt !== null && now - lastCheckedAt < MANIFEST_REFETCH_AFTER_MS) return;
    lastCheckedAt = now;
    void fetchManifest().then(() => {
      if (!stopped) onManifest(getManifest());
    });
  };

  /**
   * THE CACHED ANSWER IS IN FORCE BEFORE THE FIRST ROUND TRIP, not four seconds into it.
   *
   * Most launches on a phone are cold, so without this every launch would spend its first
   * seconds on the defaults — and the version floor would let a build through that the
   * previous launch already knew was too old. One local read, never the network.
   */
  void primeManifestFromCache().then(() => {
    if (stopped) return;
    onManifest(getManifest());
    refresh();
  });

  const unsubscribe = onForegroundChange(({ foreground }) => {
    if (foreground) refresh();
  });

  return () => {
    stopped = true;
    unsubscribe();
  };
}
